import { NgFor, NgIf, UpperCasePipe } from '@angular/common';
import { Component, computed, OnInit, signal, Signal, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatChip } from '@angular/material/chips';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { filter, take } from 'rxjs/operators';
import { EDialogWidth } from '../../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../../core/validators/trimmed-required.validator';
import { CalloutComponent, TCalloutVariant } from '../../../../shared/components/callout/callout.component';
import { DatatableComponent } from '../../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../../shared/components/datatable/datatable.interface';
import { ErrorMessagesComponent } from '../../../../shared/components/error-messages/error-messages.component';
import { IDropdownOption, MatDropdown } from '../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { ELoadStatus } from '../../../../state/state.interface';
import { FormUtilitiesService } from '../../../../utilities/form-utilities.service';
import { HttpUtilitiesService } from '../../../../utilities/http-utilities.service';
import { ESnackbar } from '../../../../utilities/snackbar.service';
import { BaseCrudPageComponent } from '../../../app/base-crud-page.component';
import { EWorkflow } from '../../../items/items.interface';
import {
  IWorkflowDiagramData,
  WorkflowDiagramDialogComponent,
} from './workflow-diagram-dialog/workflow-diagram-dialog.component';
import {
  DEFAULT_WORKFLOW_NAME,
  EWorkflowChainSeverity,
  EWorkflowChainState,
  ISaveWorkflow,
  IWorkflow,
  IWorkflowChainIssue,
  IWorkflowStepOption,
  IWorkflowTransition,
  TFormControlWorkflow,
  TTransitionFormGroup,
} from './workflows.interface';
import { IWorkflowComponentState, WorkflowsStore } from './workflows.store';

type TWorkflowRow = IWorkflow & { view: null };

type TWorkflowChainIssueView = IWorkflowChainIssue & { icon: string; variant: TCalloutVariant };

@Component({
  imports: [
    CalloutComponent,
    DatatableComponent,
    ErrorMessagesComponent,
    MatButton,
    MatCard,
    MatCardContent,
    MatChip,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatDropdown,
    MatError,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    NgFor,
    NgIf,
    ReactiveFormsModule,
    TranslateModule,
    UpperCasePipe,
    MatMiniFabButton,
  ],
  providers: [WorkflowsStore, HttpUtilitiesService],
  selector: 'app-workflows',
  standalone: true,
  styleUrl: './workflows.component.scss',
  templateUrl: './workflows.component.html',
})
export class WorkflowsComponent extends BaseCrudPageComponent<IWorkflow, IWorkflowComponentState> implements OnInit {
  @ViewChild('addEditFormDialog') addEditFormDialog!: TemplateRef<HTMLElement>;

  public readonly defaultWorkflowName = DEFAULT_WORKFLOW_NAME;

  public datatableConfigurations: IDatatableConfiguration<TWorkflowRow> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.single,
    disableCheckboxCondition: (row: TWorkflowRow): boolean => row.name === DEFAULT_WORKFLOW_NAME,
    editScope: ETableCrudScope.single,
  };

  public readonly datatableRows = computed<TWorkflowRow[]>(() =>
    this.store
      .data$()
      .map((workflow: IWorkflow) => ({ ...workflow, view: null }))
      .sort((first: TWorkflowRow, second: TWorkflowRow) => {
        if (first.name === DEFAULT_WORKFLOW_NAME) {
          return -1;
        }

        if (second.name === DEFAULT_WORKFLOW_NAME) {
          return 1;
        }

        return 0;
      }),
  );

  public readonly datatableColumns: IDatatableColumn<TWorkflowRow>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
    { id: 'view', isSortable: false },
  ];

  public readonly addEditForm: FormGroup<TFormControlWorkflow> = new FormGroup({
    name: new FormControl('', [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)]),
    startWorkflowStepId: new FormControl([] as IDropdownOption[], [Validators.required]),
    transitions: new FormArray<TTransitionFormGroup>([], [Validators.required]),
  });

  constructor(
    public readonly store: WorkflowsStore,
    public readonly formService: FormUtilitiesService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfWorkflow',
      multiContext: 'field.workflows',
      nameProperty: 'name',
      singleContext: 'field.workflowSingle',
    });
  }

  public readonly stepOptions: Signal<IDropdownOption[]> = computed(() =>
    this.store.steps$().map(
      (step: IWorkflowStepOption): IDropdownOption => ({
        id: step.id,
        name: this.stepLabel(step),
      }),
    ),
  );

  private readonly transitionSourceIds = signal<ReadonlySet<number>>(new Set());

  private readonly transitionsSnapshot = signal<ReadonlyArray<{ source: number | null; target: number | null }>>([]);
  private readonly startStepId = signal<number | null>(null);

  public readonly startStepOptions: Signal<IDropdownOption[]> = computed(() => {
    const sourceStepIds: ReadonlySet<number> = this.transitionSourceIds();
    const steps: IDropdownOption[] = this.stepOptions();

    return sourceStepIds.size ? steps.filter((step: IDropdownOption) => sourceStepIds.has(step.id)) : steps;
  });

  public readonly transitions: FormArray<TTransitionFormGroup> = this.addEditForm.controls.transitions;

  private readonly stepById: Signal<ReadonlyMap<number, IWorkflowStepOption>> = computed(
    () => new Map(this.store.steps$().map((step: IWorkflowStepOption) => [step.id, step])),
  );

  public readonly chainIssues: Signal<TWorkflowChainIssueView[]> = computed(() =>
    this.analyzeChain().map((issue: IWorkflowChainIssue) => ({
      ...issue,
      icon: issue.severity === EWorkflowChainSeverity.error ? 'error' : 'warning',
      variant: (issue.severity === EWorkflowChainSeverity.error ? 'error' : 'warning') as TCalloutVariant,
    })),
  );

  public readonly chainState: Signal<EWorkflowChainState> = computed(() => {
    if (!this.hasCompleteTransition()) {
      return EWorkflowChainState.empty;
    }

    const issues: IWorkflowChainIssue[] = this.chainIssues();

    if (issues.some((issue: IWorkflowChainIssue) => issue.severity === EWorkflowChainSeverity.error)) {
      return EWorkflowChainState.error;
    }

    return issues.length ? EWorkflowChainState.warning : EWorkflowChainState.valid;
  });

  private static readonly STATE_VARIANT: Record<EWorkflowChainState, TCalloutVariant> = {
    [EWorkflowChainState.empty]: 'neutral',
    [EWorkflowChainState.valid]: 'success',
    [EWorkflowChainState.warning]: 'warning',
    [EWorkflowChainState.error]: 'error',
  };

  private static readonly STATE_ICON: Record<EWorkflowChainState, string> = {
    [EWorkflowChainState.empty]: 'account_tree',
    [EWorkflowChainState.valid]: 'check_circle',
    [EWorkflowChainState.warning]: 'warning',
    [EWorkflowChainState.error]: 'error',
  };

  public readonly chainVariant: Signal<TCalloutVariant> = computed(
    () => WorkflowsComponent.STATE_VARIANT[this.chainState()],
  );

  public readonly chainIcon: Signal<string> = computed(() => WorkflowsComponent.STATE_ICON[this.chainState()]);

  public stepLabel(step: IWorkflowStepOption): string {
    return step.isDefault ? this.translate.instant(`system.label.${step.name}`) : step.name;
  }

  private labelOf(stepId: number): string {
    const step: IWorkflowStepOption | undefined = this.stepById().get(stepId);

    return step ? this.stepLabel(step) : `#${stepId}`;
  }

  private hasCompleteTransition(): boolean {
    return this.transitionsSnapshot().some((row) => row.source !== null && row.target !== null);
  }

  private analyzeChain(): IWorkflowChainIssue[] {
    const rows: ReadonlyArray<{ source: number | null; target: number | null }> = this.transitionsSnapshot();
    const complete: { source: number; target: number }[] = rows.filter(
      (row): row is { source: number; target: number } => row.source !== null && row.target !== null,
    );

    if (!complete.length) {
      return [];
    }

    const issues: IWorkflowChainIssue[] = [];
    const start: number | null = this.startStepId();

    // Nodes and adjacency (self-loops excluded from routing — they never advance an item).
    const nodes = new Set<number>();
    const adjacency = new Map<number, Set<number>>();
    const seenPairs = new Set<string>();
    const duplicatePairs = new Set<string>();
    const selfLoops = new Set<number>();

    for (const { source, target } of complete) {
      nodes.add(source);
      nodes.add(target);

      const pairKey = `${source}-${target}`;

      if (seenPairs.has(pairKey)) {
        duplicatePairs.add(pairKey);
      }
      seenPairs.add(pairKey);

      if (source === target) {
        selfLoops.add(source);

        continue;
      }

      const targets: Set<number> = adjacency.get(source) ?? new Set<number>();
      targets.add(target);
      adjacency.set(source, targets);
    }

    if (start !== null) {
      nodes.add(start);
    }

    // --- Errors ---
    if (start === null) {
      issues.push({ messageKey: 'workflowChain.noStart', severity: EWorkflowChainSeverity.error });
    } else if (!adjacency.get(start)?.size) {
      issues.push({ messageKey: 'workflowChain.startNoOutgoing', severity: EWorkflowChainSeverity.error });
    } else {
      const reachable: Set<number> = this.reachableFrom(start, adjacency);
      const unreachable: number[] = [...nodes].filter((id: number) => id !== start && !reachable.has(id));

      if (unreachable.length) {
        issues.push({
          messageKey: 'workflowChain.unreachable',
          params: { statuses: this.joinLabels(unreachable) },
          severity: EWorkflowChainSeverity.error,
        });
      }
    }

    // --- Warnings ---
    if (selfLoops.size) {
      issues.push({
        messageKey: 'workflowChain.selfLoop',
        params: { statuses: this.joinLabels([...selfLoops]) },
        severity: EWorkflowChainSeverity.warning,
      });
    }

    if (duplicatePairs.size) {
      issues.push({
        messageKey: 'workflowChain.duplicate',
        params: { statuses: [...duplicatePairs].map((pair: string) => this.pairLabel(pair)).join(', ') },
        severity: EWorkflowChainSeverity.warning,
      });
    }

    // Dead-ends: reachable statuses with no way out that are not a terminal (Done) status. The start
    // is excluded — its lack of an outgoing edge is already reported as an error above.
    const deadEnds: number[] = [...nodes].filter(
      (id: number) =>
        id !== start && !adjacency.get(id)?.size && this.stepById().get(id)?.statusCategory !== EWorkflow.done,
    );

    if (deadEnds.length) {
      issues.push({
        messageKey: 'workflowChain.deadEnd',
        params: { statuses: this.joinLabels(deadEnds) },
        severity: EWorkflowChainSeverity.warning,
      });
    }

    const incompleteCount: number = rows.length - complete.length;

    if (incompleteCount > 0) {
      issues.push({
        messageKey: 'workflowChain.incompleteRows',
        params: { count: incompleteCount },
        severity: EWorkflowChainSeverity.warning,
      });
    }

    return issues;
  }

  private reachableFrom(start: number, adjacency: Map<number, Set<number>>): Set<number> {
    const reachable = new Set<number>([start]);
    const queue: number[] = [start];

    while (queue.length) {
      const current: number = queue.shift() as number;

      for (const next of adjacency.get(current) ?? []) {
        if (!reachable.has(next)) {
          reachable.add(next);
          queue.push(next);
        }
      }
    }

    return reachable;
  }

  private joinLabels(ids: number[]): string {
    return ids.map((id: number) => this.labelOf(id)).join(', ');
  }

  private pairLabel(pairKey: string): string {
    const [source, target] = pairKey.split('-').map(Number);

    return `${this.labelOf(source)} → ${this.labelOf(target)}`;
  }

  private toStepOption(stepId: number | null): IDropdownOption[] {
    if (stepId === null) {
      return [];
    }

    const step: IWorkflowStepOption | undefined = this.store.steps$().find((s: IWorkflowStepOption) => s.id === stepId);

    return step ? [{ id: step.id, name: this.stepLabel(step) }] : [];
  }

  public ngOnInit(): void {
    this.store.loadSteps();

    this.subscriptions.push(this.transitions.valueChanges.subscribe(() => this.syncTransitionSourceIds()));

    this.subscriptions.push(
      this.addEditForm.controls.startWorkflowStepId.valueChanges.subscribe((value) =>
        this.startStepId.set(value?.[0]?.id ?? null),
      ),
    );

    this.subscriptions.push(
      this.datatableParams$.pipe(filter(Boolean)).subscribe((datatable) => {
        this.store.loadItems({
          fields: ['name', 'startWorkflowStepId'],
          join: ['workflowStepAssignments||workflowStepId,nextWorkflowStepId'],
          limit: datatable.limit,
          page: datatable.page,
          sort: [datatable.sort],
          ...(datatable.search ? { search: { searchedFields: ['name'], searchText: datatable.search } } : {}),
        });
      }),
    );
  }

  public initializeAdd(): void {
    // Statuses may have been added on the Statuses tab since this component loaded.
    this.store.loadSteps();
    this.setupForAdd();
    this.addEditForm.enable();
    this.transitions.clear();
    this.addEditForm.reset({ name: '', startWorkflowStepId: [] });
    this.addTransitionRow();

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.medium,
    });
  }

  public initializeEdit(): void {
    const [item] = this.selectedRows();

    if (!item || item.name === DEFAULT_WORKFLOW_NAME) {
      return;
    }

    // Statuses may have been added on the Statuses tab since this component loaded.
    this.store.loadSteps();
    this.setupForEdit();
    this.addEditForm.enable();
    this.transitions.clear();
    this.addEditForm.controls.name.reset(item.name);
    this.addEditForm.controls.startWorkflowStepId.reset(this.toStepOption(item.startWorkflowStepId));
    this.buildTransitionRows(item.workflowStepAssignments);

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.medium,
    });
  }

  public openDiagram(row: IWorkflow): void {
    this.store.loadOne(row.id);

    this.subscriptions.push(
      this.store.selectedWorkflowStatus$
        .pipe(
          filter((status: ELoadStatus) => status === ELoadStatus.success || status === ELoadStatus.failure),
          take(1),
        )
        .subscribe((status: ELoadStatus) => {
          const loaded: IWorkflow | null = this.store.selectedWorkflow$();
          const workflow: IWorkflow = status === ELoadStatus.success && loaded ? loaded : row;

          const data: IWorkflowDiagramData = {
            name:
              workflow.name === DEFAULT_WORKFLOW_NAME ? this.translate.instant('field.defaultWorkflow') : workflow.name,
            startWorkflowStepId: workflow.startWorkflowStepId,
            steps: this.store.steps$(),
            transitions: workflow.workflowStepAssignments,
          };

          this.matDialog.open(WorkflowDiagramDialogComponent, {
            data,
            maxWidth: '100%',
            restoreFocus: false,
            width: EDialogWidth.medium,
          });
        }),
    );
  }

  public addTransitionRow(): void {
    this.transitions.push(
      new FormGroup({
        nextWorkflowStepId: new FormControl([] as IDropdownOption[], [Validators.required]),
        workflowStepId: new FormControl([] as IDropdownOption[], [Validators.required]),
      }),
    );
  }

  public removeTransitionRow(index: number): void {
    this.transitions.removeAt(index);
  }

  public onFormDialogSubmit(): void {
    if (!this.addEditForm.valid) {
      this.addEditForm.markAllAsTouched();

      return;
    }

    // The chain callout already lists the errors; block submit so the API never rejects a broken chain
    // (e.g. a start status without an outgoing transition).
    if (this.chainState() === EWorkflowChainState.error) {
      this.snackbarService.open(this.translate.instant('workflowChain.blockSubmit'), ESnackbar.error);

      return;
    }

    const payload: ISaveWorkflow = this.formatPayload();

    if (!payload.transitions.length) {
      return;
    }

    if (!this.isEditDialog()) {
      this.store.createOne(payload);
    } else {
      const [item] = this.selectedRows();
      this.store.updateOne({ dto: payload, id: item.id });
    }

    this.matDialog.closeAll();
  }

  private syncTransitionSourceIds(): void {
    const ids = new Set<number>();
    const snapshot: { source: number | null; target: number | null }[] = [];

    for (const row of this.transitions.controls) {
      const source: number | null = row.controls.workflowStepId.value?.[0]?.id ?? null;
      const target: number | null = row.controls.nextWorkflowStepId.value?.[0]?.id ?? null;

      if (source != null) {
        ids.add(source);
      }

      snapshot.push({ source, target });
    }

    this.transitionSourceIds.set(ids);
    this.transitionsSnapshot.set(snapshot);
  }

  private buildTransitionRows(assignments: IWorkflowTransition[]): void {
    if (!assignments.length) {
      this.addTransitionRow();

      return;
    }

    for (const assignment of assignments) {
      this.transitions.push(
        new FormGroup({
          nextWorkflowStepId: new FormControl(this.toStepOption(assignment.nextWorkflowStepId), [Validators.required]),
          workflowStepId: new FormControl(this.toStepOption(assignment.workflowStepId), [Validators.required]),
        }),
      );
    }
  }

  private formatPayload(): ISaveWorkflow {
    const uniqueTransitions = new Map<string, ISaveWorkflow['transitions'][number]>();

    for (const row of this.transitions.controls) {
      const workflowStepId: number | null = row.controls.workflowStepId.value?.[0]?.id ?? null;
      const nextWorkflowStepId: number | null = row.controls.nextWorkflowStepId.value?.[0]?.id ?? null;

      if (workflowStepId === null || nextWorkflowStepId === null) {
        continue;
      }

      uniqueTransitions.set(`${workflowStepId}-${nextWorkflowStepId}`, { nextWorkflowStepId, workflowStepId });
    }

    const transitions = [...uniqueTransitions.values()];

    return {
      name: FormUtilitiesService.trimIfString(this.addEditForm.controls.name.value ?? ''),
      startWorkflowStepId: this.addEditForm.controls.startWorkflowStepId.value?.[0]?.id!,
      transitions,
    };
  }
}
